// 画布工具跑在聊天页。真正读写 player 的是编辑器 iframe，经 shell 转发。
import { tool } from '@langchain/core/tools';
import { z } from 'zod';

const TIMEOUT_MS = 15000;
const waiters = new Map();
let boundWindow = null;
let projectId = null;

function host() {
    if (typeof window === 'undefined') return null;
    if (window.parent && window.parent !== window) return window.parent;
    return window.opener || null;
}

function ensureListener() {
    if (typeof window === 'undefined' || boundWindow === window) return;
    boundWindow = window;
    window.addEventListener('message', event => {
        const target = host();
        if (!target || event.source !== target || event.origin !== location.origin) return;
        const data = event.data;
        if (!data || data.channel !== 'nle-chat' || data.kind !== 'toolResult') return;
        const pending = waiters.get(data.id);
        if (!pending) return;
        waiters.delete(data.id);
        clearTimeout(pending.timer);
        pending.resolve(data.result);
    });
}

export function setEditorProject(id) {
    projectId = id || null;
}

export function abortEditorCalls(reason) {
    for (const [id, pending] of waiters) {
        clearTimeout(pending.timer);
        waiters.delete(id);
        pending.reject(reason instanceof Error ? reason : new Error(reason || '编辑器调用已取消'));
    }
}

function callEditor(action, params) {
    ensureListener();
    const target = host();
    if (!target) return Promise.reject(new Error('聊天窗口未连接编辑器'));
    if (!projectId) return Promise.reject(new Error('当前没有打开的工程'));
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            waiters.delete(id);
            reject(new Error(`编辑器调用超时：${action}`));
        }, TIMEOUT_MS);
        waiters.set(id, { resolve, reject, timer });
        target.postMessage({
            channel: 'nle-chat',
            kind: 'frontend',
            call: { id, token: id, projectId, action, params: params || {} },
        }, location.origin);
    }).then(result => {
        if (!result || result.success === false) throw new Error(result?.error || '编辑器调用失败');
        return result;
    });
}

const layerId = z.string().describe('图层 ID');
const trackId = z.string().describe('轨道 ID');
const timeUs = z.number().nonnegative().describe('时间，微秒');
const TRACK_TYPES = ['video', 'audio', 'text', 'image', 'svg', 'sticker', 'effect', 'filter'];

// 给动态提示词用，不是模型工具。每次模型调用前读一次当时的轨道。
export async function readCanvas() {
    if (!projectId) return { loaded: false, message: '当前没有打开的工程' };
    try {
        return { loaded: true, ...await callEditor('listTracks', {}) };
    } catch (error) {
        return { loaded: false, message: error.message };
    }
}

export const EDITOR_TOOLS = [
    tool(async ({ timeUs: time }) => JSON.stringify(await callEditor('setCurrentTime', { timeUs: time })), {
        name: 'set_current_time',
        description: '把播放头跳到指定时间并刷新画面。',
        schema: z.object({ timeUs }),
    }),
    tool(async ({ type, index }) => JSON.stringify(await callEditor('addTrack', { type, index })), {
        name: 'add_track',
        description: '新建一条空轨道。index 是叠放位置，也是渲染顺序。',
        schema: z.object({
            type: z.enum(TRACK_TYPES),
            index: z.number().int().nonnegative().optional().describe('0 在最底下、最先渲染；越大越靠上，后渲染。省略则追加到末尾'),
        }),
    }),
    tool(async ({ trackId: id, visible, muted, index }) => JSON.stringify(await callEditor('updateTrack', {
        trackId: id, visible, muted, index,
    })), {
        name: 'update_track',
        description: '修改轨道的可见性、静音或顺序。不能改轨道类型。',
        schema: z.object({
            trackId,
            visible: z.boolean().optional(),
            muted: z.boolean().optional(),
            index: z.number().int().nonnegative().optional().describe('0 在最底下、最先渲染；越大越靠上，后渲染'),
        }),
    }),
    tool(async ({ trackId: id }) => JSON.stringify(await callEditor('deleteTrack', { trackId: id })), {
        name: 'delete_track',
        description: '删除一条轨道及其上的全部图层。',
        schema: z.object({ trackId }),
    }),
    tool(async (params) => JSON.stringify(await callEditor('addLayer', params)), {
        name: 'add_layer',
        description: '添加图层。指定 trackId 则放进该轨道；否则新建轨道，位置由 index 决定。',
        schema: z.object({
            materialId: z.string().optional().describe('工程里已有的素材 ID'),
            text: z.string().optional().describe('不传 materialId 时作为新文字的文案'),
            trackId: z.string().optional(),
            index: z.number().int().nonnegative().optional().describe('新建轨道时的下标。0 在最底下、最先渲染；越大越靠上，后渲染。省略则追加到末尾'),
            startUs: timeUs.optional().describe('入点，默认当前播放头'),
            durationUs: z.number().positive().optional().describe('时长，微秒。视频和音频默认用素材时长，其它默认 3000000'),
            createTrack: z.boolean().optional(),
        }).refine(value => value.materialId || value.text, { message: '需要 materialId 或 text' }),
    }),
    tool(async (params) => JSON.stringify(await callEditor('updateLayer', params)), {
        name: 'update_layer',
        description: '修改已有图层的文案、画面属性、时间或所属轨道。裁剪不改变播放速度。',
        schema: z.object({
            layerId,
            text: z.string().optional(),
            visible: z.boolean().optional(),
            muted: z.boolean().optional(),
            volume: z.number().optional(),
            alpha: z.number().optional(),
            rotation: z.number().optional(),
            scaleX: z.number().optional(),
            scaleY: z.number().optional(),
            transformX: z.number().optional(),
            transformY: z.number().optional(),
            startUs: timeUs.optional().describe('移动后的入点'),
            trackId: z.string().optional().describe('移入的同类型轨道'),
            createTrack: z.boolean().optional().describe('为 true 时新建同类型轨道再移入，位置由 index 决定'),
            index: z.number().int().nonnegative().optional().describe('新建轨道时的下标。0 在最底下、最先渲染；越大越靠上，后渲染'),
            edge: z.enum(['left', 'right']).optional().describe('left 裁入点，right 裁出点'),
            timeUs: z.number().optional().describe('裁剪这一端的新位置，微秒'),
        }),
    }),
    tool(async ({ layerId: id }) => JSON.stringify(await callEditor('deleteLayer', { layerId: id })), {
        name: 'delete_layer',
        description: '从轨道上删除一个图层。它若是转场的后一段，转场引用一并去掉。',
        schema: z.object({ layerId }),
    }),
];
